import { forwardRef, useImperativeHandle } from 'react';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { useFormControls, lt } from '@mews/framework';
import { Banner, Stack } from '@optimus-web/core';
import { useDefaultFormContext } from '../useDefaultFormValues';
import { CommonProcessPaymentInvoiceIdsModel } from '../ProcessPaymentFormSchema';
import { PaymentCommonSection } from '../../../common/PaymentCommonSection';

interface Props {
    onSuccess?: () => void;
    onSubmitting?: () => void;
    onSubmitted?: () => void;
}

export const ZeroInvoiceTotalForm = forwardRef(({ onSuccess, onSubmitting, onSubmitted }: Props, ref) => {
    const { mutate: mutateIssuePaymentConfirmation } = useRepositoryMutation(
        'billing',
        'issuePaymentConfirmationWithoutPayment',
        {
            onMutate: () => {
                onSubmitting?.();
            },
            onSuccess: () => {
                onSuccess?.();
            },
            onSettled: () => {
                onSubmitted?.();
            },
        }
    );

    const { accountId, defaultFormValues } = useDefaultFormContext();

    const { renderFormContent, submit, hasDirtyFields } = useFormControls<CommonProcessPaymentInvoiceIdsModel>({
        onSubmit: ({ invoiceIds }) => {
            mutateIssuePaymentConfirmation({
                invoiceIds,
                ownerId: accountId,
            });
        },
        defaultValues: defaultFormValues,
    });

    useImperativeHandle(
        ref,
        () => ({
            submit,
            hasDirtyFields,
        }),
        [submit, hasDirtyFields]
    );

    return renderFormContent(
        <Stack spacing="300" vertical>
            <PaymentCommonSection />
            <Banner
                variant="info"
                primaryText={lt('InvoicesBalanceOffsetZero')}
                secondaryText={lt('InvoicesBalanceOffsetZeroHelper')}
            />
        </Stack>
    );
});
